import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { apiService } from '../services/api';
import { resolveApiError } from '../utils/i18n';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';

// ponytail: 管理員建立/重設帳號後，密碼為系統生成，需強制用戶登入後立即修改
export const ForceChangePasswordGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  const { showAlert } = useAlert();
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!user?.mustChangePassword) return <>{children}</>;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      showAlert({ type: 'warning', message: t('forceChangePassword.mismatch', '新密碼與確認密碼不一致') });
      return;
    }
    try {
      setSubmitting(true);
      await apiService.users.changePassword({ currentPassword, newPassword, confirmPassword });
      showAlert({ type: 'success', message: t('forceChangePassword.success', '密碼已修改，請重新登入') });
      await logout();
      window.location.href = '/login-form';
    } catch (error: any) {
      showAlert({ type: 'warning', message: resolveApiError(error?.response?.data?.errorCode, error?.response?.data?.message, t) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <Card style={{ maxWidth: 420, width: '100%' }}>
        <CardHeader>
          <CardTitle>{t('forceChangePassword.title', '請修改您的密碼')}</CardTitle>
          <CardDescription>{t('forceChangePassword.description', '您的帳號密碼為系統生成，首次登入需先設定新密碼才能繼續使用。')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">{t('forceChangePassword.currentPassword', '目前密碼')}</Label>
              <Input id="currentPassword" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword">{t('forceChangePassword.newPassword', '新密碼（6-20 位）')}</Label>
              <Input id="newPassword" type="password" minLength={6} maxLength={20} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t('forceChangePassword.confirmPassword', '確認新密碼')}</Label>
              <Input id="confirmPassword" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
            </div>
            <Button type="submit" disabled={submitting} className="w-full">
              {submitting ? t('common.processing', '處理中...') : t('forceChangePassword.submit', '確認修改')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
